import { StackLayout } from '@euler/app/components/StackLayout';
import { StatusLabel } from '@euler/app/components/StatusLabel';
import { Typography } from '@euler/app/components/typography';
import { MemberChatButtonByName } from '@euler/app/flows/chat/components/MemberChatButtonByName';
import { useDeliveryCheckFlows } from '@euler/app/flows/task/functions/task-detail/delivery-check/useDeliveryCheckFlows';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useTaskExtraInfo } from '@euler/app/flows/task/functions/useTaskExtraInfo';
import { TaskCardTitle } from '@euler/app/flows/task/pages/detail/components/TaskCardTitle';
import { TaskDetailSection } from '@euler/app/flows/task/pages/detail/components/TaskDetailSection';
import { TimeElapsedLabel } from '@euler/app/flows/task/pages/detail/components/TimeElapsedLabel';
import { Avatar } from '@euler/components/Avatar';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { TableView } from '@euler/components/TableView';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  CommonTaskStatus,
  DeliveryCheckSceneType,
  InspectionTaskStatus,
  SamplingCheckResultType,
} from '@euler/model/enum';
import { timeUtilNow } from '@euler/utils/formatters';
import Color from 'color';
import { memo } from 'react';
import { View } from 'react-native';

export const WiredDeliveryCheckInfo = memo(() => {
  const { rpx } = useResponsiveContext();
  const {
    detail: { deliveryCheck, basicInfo },
    profile,
  } = useTaskContext();

  const extraInfo = useTaskExtraInfo();

  const { flows, onDeliveryCheckFlowPress } = useDeliveryCheckFlows();

  if (flows.length === 0) {
    return null;
  }

  return (
    <TaskDetailSection
      title={
        <TaskCardTitle
          text="交车质检"
          status={
            profile.store.isIndependentDeliveryCheckEnabled
              ? deliveryCheck.status === CommonTaskStatus.Finished &&
                basicInfo.status === InspectionTaskStatus.Finished
                ? CommonTaskStatus.Finished
                : CommonTaskStatus.Pending
              : deliveryCheck.status
          }
          shared={extraInfo?.reportShareSummary?.deliveryCheck}
          viewed={extraInfo?.reportViewSummary?.deliveryCheck}
        />
      }
      bodyStyle={{
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
      }}
    >
      <TableView>
        {flows.map(flow => (
          <TableView.Item
            key={flow.type}
            title={flow.name}
            titleStyle={{ marginBottom: rpx(5) }}
            titleTextStyle={{ fontSize: rpx(17), lineHeight: rpx(20) }}
            subTitle={
              flow.tip ? (
                <StackLayout
                  direction="row"
                  alignItems="center"
                  ml={rpx(5)}
                  gap={rpx(2)}
                  css={`
                    align-self: flex-start;
                    padding: 0 ${rpx(7)}px;
                    border-radius: ${rpx(18)}px;
                    background-color: ${Color(
                      flow.tipColor ?? StatusColors.Notice,
                    )
                      .alpha(0.15)
                      .toString()};
                    justify-content: center;
                  `}
                >
                  <Icon
                    name="information-outline"
                    size={10}
                    color={flow.tipColor ?? StatusColors.Warn}
                    css={`
                      top: -0.25px;
                    `}
                  />
                  <Typography.Text
                    color={flow.tipColor ?? StatusColors.Warn}
                    size={rpx(11)}
                    lineHeight={18}
                    top={-1}
                  >
                    {flow.tip}
                  </Typography.Text>
                </StackLayout>
              ) : null
            }
            onPress={() => onDeliveryCheckFlowPress(flow)}
            note={
              <View>
                <Typography.Label
                  size={rpx(13)}
                  lineHeight={rpx(18)}
                  color={p => p.components.table.item.noteColor}
                  numberOfLines={1}
                  light
                >
                  {flow.description}
                </Typography.Label>

                {flow.author != null && flow.timestamp != null && (
                  <View
                    css={`
                      margin-top: ${rpx(4)}px;
                      flex-direction: row;
                      align-items: center;
                    `}
                  >
                    <Avatar
                      name={flow.author}
                      css={`
                        width: ${rpx(16)}px;
                        height: ${rpx(16)}px;
                        border-radius: ${rpx(8)}px;
                        margin-right: ${rpx(3)}px;
                      `}
                    />
                    <Typography.Label
                      size={rpx(12)}
                      regular
                      color={p => p.components.table.item.noteColor}
                    >
                      {`${flow.author}`}
                    </Typography.Label>
                    <MemberChatButtonByName
                      name={flow.author}
                      size={rpx(14)}
                      style={{ marginLeft: rpx(4) }}
                    />
                    <Typography.Label
                      size={rpx(12)}
                      color={p => p.components.table.item.noteColor}
                      opacity={0.5}
                      ml={rpx(5)}
                      light
                    >
                      {`${timeUtilNow(flow.timestamp)}`}
                    </Typography.Label>
                  </View>
                )}
              </View>
            }
            noteStyle={{ fontSize: rpx(12) }}
            iconStyle={{ marginRight: rpx(10) }}
            detailIcon="disclosure"
            contentContainerStyle={{
              paddingTop: rpx(15),
              paddingBottom: rpx(15),
            }}
            icon={
              <Center width={rpx(32)} height={rpx(32)}>
                {flow.icon}
              </Center>
            }
            detail={
              <View
                css={`
                  align-items: flex-end;
                  justify-content: center;
                  margin-left: ${rpx(15)}px;
                `}
              >
                <StatusLabel
                  status={flow.status}
                  continuable
                  continueText={
                    flow.type === DeliveryCheckSceneType.SamplingCheck &&
                    flow.target?.status === CommonTaskStatus.Finished &&
                    flow.target.resultType === SamplingCheckResultType.Failed
                      ? '不合格'
                      : '继续检验'
                  }
                  text={
                    flow.type === DeliveryCheckSceneType.SamplingCheck &&
                    flow.target?.status === CommonTaskStatus.Finished &&
                    flow.target.resultType === SamplingCheckResultType.Failed
                      ? '不合格'
                      : undefined
                  }
                  style={
                    flow.type === DeliveryCheckSceneType.SamplingCheck &&
                    flow.target?.status === CommonTaskStatus.Finished &&
                    flow.target.resultType === SamplingCheckResultType.Failed
                      ? { backgroundColor: StatusColors.Danger }
                      : undefined
                  }
                />
                {flow.startedAt != null && (
                  <TimeElapsedLabel
                    flow={{
                      ...flow,
                      status:
                        flow.status === CommonTaskStatus.Pending
                          ? 'pending'
                          : flow.status === CommonTaskStatus.InProgress
                          ? 'inprogress'
                          : 'finished',
                    }}
                  />
                )}
              </View>
            }
          />
        ))}
      </TableView>
    </TaskDetailSection>
  );
});
